// ROUTE CODE EDITED 07/16/2026
/**
 * 
 * route-i18n.js — Multi-language text localization for the Route "Preferred Checkout"
 * cart widget (App Embed / v3). Currently supports: English (no-op), French.
 *
 * WHY THIS EXISTS
 *   Route's cart widget renders only in English. Its visible copy is not overridable via
 *   theme config (it comes from Route's API through React props). This script rewrites the
 *   widget's rendered English text nodes into the storefront language (French) on the French
 *   storefront only.
 *
 * DESIGN (read before editing)
 *   - HYBRID MATCHING: the default is exact content matching (normalized English string ->
 *     translation). A small PATTERN path handles strings with an inline dynamic value (e.g. a
 *     cashback rate) that a literal key can't express. The header amount is NOT a pattern:
 *     Route renders it in a separate text node, so the header is a plain content match and the
 *     amount is left untouched.
 *   - STATELESS / IDEMPOTENT: no "translated" marker is set on the DOM. Re-passes are safe
 *     because a translated value is never itself a key (lookup miss = cheap no-op). This is
 *     deliberate: Route re-renders via React and reuses DOM elements while resetting their
 *     text, so any persistent marker would go stale and cause missed re-translation. The
 *     MutationObserver re-applies after every re-render; correctness comes from re-scanning,
 *     not from remembering.  ==> INVARIANT: within each locale table, no value may equal a key.
 *   - WIDGET VARIANTS: Route resolves a different variant from returns eligibility (gated on
 *     Shopify.country; returns only in eligible countries e.g. CA/US). Changing country/currency
 *     flips the variant and its copy. Both the RETURNS and STANDARD variants are covered per
 *     locale; if a variant's strings are missing, that variant falls back to English.
 *
 * SCOPE & SAFETY (must be preserved on any edit)
 *   - Only rewrites text-node values and aria-labels inside Route-owned elements (SELECTORS).
 *     Never adds/removes elements, never matches checkout-button selectors, never calls Route,
 *     so it cannot trigger Route's re-mount logic or affect checkout.
 *   - Fail-safe: every entry point is wrapped in try/catch. Any error leaves the original
 *     English rendering functional. Removing this file reverts behavior completely.
 *   - Amounts/number formatting are intentionally left to Route (Route currently formats with a
 *     hard-coded en-US locale).
 *
 * MAINTENANCE
 *   - EXACT-MATCH DEPENDENCY: content keys must equal the rendered string (normalized). If
 *     Route rewords copy or splits a string across nodes, that entry silently falls back to
 *     English. The preview-only drift logger (below) surfaces this during QA.
 *   - SPLIT-NODE FRAGILITY: the cashback benefit row renders as two nodes ("Package" +
 *     " Protection by") with a logo <img> between them, from Route's shared UI package. It is
 *     handled via two fragment keys per locale that must reconstruct with correct word order.
 *   - LANGUAGE SOURCING: French translations are verified/approved unless marked otherise.
 *   - DRIFT LOGGER: add ?route_i18n_debug=1, open in the theme editor (Shopify.designMode), or
 *     preview via preview_theme_id to console-log untranslated strings found in Route scope.
 *
 * DEPLOYMENT
 *   - Load via theme.liquid on non-English storefronts. English is a no-op regardless.
 *   - Some of the styling for the Route widget is baased on locating the checkout button with 
 *     text-matching. When the checkout button renders in French, this breaks some of the styling
 *     on the CWC link, so additional styling needs to be added to the CWC link via the Route
 *     Theme App Embed "Custom CSS" field.
 */
(function () {
  "use strict";

  var DEBOUNCE_MS = 50;

  // Sentinel for "key exists but no approved translation yet" => leave English, log in debug.
  var TODO = null;

  // ===========================================================================================
  // LOCALE TABLES
  // Each locale: { text: {EN -> translation}, aria: {EN -> translation}, patterns: [{re, tr}] }
  // Grouped by variant.
  // ===========================================================================================
  var LOCALES = {

    // English storefront is a no-op; table intentionally empty.
    en: { text: {}, aria: {}, patterns: [] },

    // ---------------------------------------------------------------------------------------
    // FRENCH
    // ---------------------------------------------------------------------------------------
    fr: {
      text: {
        // RETURNS variant
        "Free Returns and your order is protected": "Retours gratuits et votre commande est protégée",
        "for": "pour",
        "See what's included": "Voir ce qui est inclus",
        "BENEFITS:": "AVANTAGES :",
        "Free return shipping and no restocking fees": "Expédition de retour gratuite et sans frais de réapprovisionnement",
        "Free return shipping and": "Expédition de retour gratuite et",
        "no restocking fees": "sans frais de réapprovisionnement",
        "Damage, loss, theft coverage": "Couverture en cas de dommages, de perte ou de vol",
        "Fast refund or replacement": "Remboursement ou remplacement rapide",
        "Checkout without Coverage": "Passer à la caisse sans protection",
        "See": "Voir",
        "Details": "Détails",
        // Modal
        "We've got you covered.": "Nous vous avons couvert.",
        "Package Protection": "Protection des colis",
        "Coverage in case your package is damaged in transit, stolen, or doesn't arrive.": "Couverture au cas où votre colis est endommagé en transit, volé, ou n'arrive pas.",
        "Instant Issue Resolution": "Résolution instantanée des problèmes",
        "Get a refund or replacement in just a few clicks.": "Obtenez un remboursement ou un remplacement en quelques clics.",
        "24/7 Claim Support": "Support de réclamation 24/7",
        "Route is here for you whenever an issue arises — with an average response time of under an hour.": "Route est là pour vous chaque fois qu'un problème survient — avec un temps de réponse moyen de moins d'une heure.",
        "File A Claim": "Déposer une réclamation",
        "User Privacy": "Confidentialité de l'utilisateur",
        "Terms of Service": "Conditions de service",
        // Modal
        "Exchanges and Returns": "Échanges et retours",
        "With Route Package protection, you get return shipping and restocking fees covered.": "Avec la protection des colis Route, les frais d'expédition de retour et de réapprovisionnement sont couverts.",
        "Free Returns and Route Package Protection saves you time and money.": "Les retours gratuits et la protection des colis Route vous font gagner du temps et de l'argent.",
        // Legal
        "Route package protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery, where Route traces the transportation. Route App, Inc. (Route) is the named insured on the shipping insurance policy (Policy); Route Insurance Services (RIS), Route's licensed producer entity, procures the policy from SEG Insurance Ltd. Route, through RIS, may receive compensation for its services. A portion of the amount collected by Route is sent to the merchant to cover returns.": "La protection des colis Route étend la couverture pour les achats en ligne qui sont perdus ou endommagés en transit, ou volés immédiatement après la preuve de livraison du transporteur où Route trace le transport. Route App, Inc. (Route) est l'assuré nommé sur la police d'assurance expédition (« Police ») ; Route Insurance Services (« RIS »), l'entité productrice agréée de Route, obtient la police auprès de SEG Insurance Ltd. Route, par l'intermédiaire de RIS, peut recevoir une compensation pour ses services. Une partie du montant collecté par Route est versée au marchand pour couvrir les retours.",

        // STANDARD / shipping-protection variant
        "Your order is protected for": "Votre commande est protégée pour",
        "Order protected for": "Commande protégée pour",
        "Package": "Protection des colis",     // fragment 1 (see SPLIT-NODE note)
        "Protection by": "par",                // fragment 2 -> "Protection des colis par <logo>"
        "Shipping Protection gives you peace of mind while saving you time and money.": "La protection de l'expédition vous offre tranquillité d'esprit tout en vous faisant gagner du temps et de l'argent.",
        // Legal
        "Route shipping protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery where Route traces the transportation. Route App, Inc. (Route) is the named insured on the shipping insurance policy (Policy); Route Insurance Solutions (RIS), Route's licensed producer entity, procures the Policy from SEG Insurance Ltd. Route, through RIS, and may receive compensation for its services.": "La protection d'expédition Route étend la couverture pour les achats en ligne qui sont perdus ou endommagés en transit, ou volés immédiatement après la preuve de livraison du transporteur où Route trace le transport. Route App, Inc. (Route) est l'assuré nommé sur la police d'assurance expédition (« Police ») ; Route Insurance Services (« RIS »), l'entité productrice agréée de Route, obtient la police auprès de SEG Insurance Ltd. Route, par l'intermédiaire de RIS, peut recevoir une compensation pour ses services."
      },
      aria: {
        "route shipping protection selector element": "Sélecteur de protection d'expédition Route",
        "Coverage Options": "Options de couverture",
        "See what's included": "Voir ce qui est inclus",
        "Checkout Without Coverage": "Passer à la caisse sans protection"
      },
      // PATTERN example (UNVERIFIED in cart — harmless if it never matches). Translates a line
      // with an inline dynamic value ($1 = captured rate). Confirm the cashback variant renders
      // this in the cart before relying on it.
      // patterns: [
      //   {
      //     re: /^Earn (.+?) back on your item total — sent to your inbox after purchase to use on your next order\. Cash Back is provided by the merchant\.$/,
      //     tr: "Gagnez $1 sur le total de vos articles — envoyé dans votre boîte mail après l'achat à utiliser sur votre prochaine commande. Le cashback est fourni par le marchand."
      //   }
      // ]
    }
  };

  // --- Route-owned root selectors (scoping). The modal is a body-level portal outside
  //     [data-route-widget], so .route-modal must be listed explicitly. ------------------------
  var SELECTORS = [
    "[data-route-widget]",
    "[data-route-widget-header]",
    "[data-route-widget-cwc]",
    "[data-checkout-without-coverage-link]",
    "[data-route-cloned-button]",
    '[data-testid="route-modal"]',
    ".route-modal"
  ].join(",");

  // ===========================================================================================
  // ENGINE
  // ===========================================================================================

  // Resolve storefront language to a supported base locale, else "en" (no-op).
  function resolveLocale() {
    try {
      var raw = (window.Shopify && Shopify.locale) || document.documentElement.lang || "";
      raw = String(raw).toLowerCase();
      if (!raw && /^\/en(\/|$)/.test(location.pathname)) raw = "en";
      var base = raw.split("-")[0];
      return (base === "fr" || base === "en") ? base : "en";
    } catch (e) {
      return "en";
    }
  }

  function isDebug() {
    try {
      return /[?&]route_i18n_debug=1(\b|&|$)/.test(location.search)
        || (window.Shopify && Shopify.designMode === true)
        || /[?&]preview_theme_id=/.test(location.search)
        || window.__ROUTE_I18N_DEBUG__ === true;
    } catch (e) {
      return false;
    }
  }

  var LOCALE = resolveLocale();
  if (LOCALE === "en" || !LOCALES[LOCALE]) return; // English / unsupported => no-op

  var DEBUG = isDebug();
  var TABLE = LOCALES[LOCALE];
  var TEXT = new Map(Object.entries(TABLE.text || {}));
  var ARIA = new Map(Object.entries(TABLE.aria || {}));
  var PATTERNS = TABLE.patterns || [];

  // Reverse set of translated values, so the drift logger doesn't flag already-translated text.
  var VALUE_SET = new Set();
  TEXT.forEach(function (v) { if (v != null) VALUE_SET.add(v); });

  // Idempotency invariant check (debug only): no translated value may also be a key.
  if (DEBUG) {
    try {
      TEXT.forEach(function (v) {
        if (v != null && TEXT.has(v)) {
          console.warn("[route-i18n] IDEMPOTENCY RISK (" + LOCALE + "): value is also a key:", JSON.stringify(v));
        }
      });
    } catch (e) { /* ignore */ }
  }

  var _logged = new Set();
  function logDrift(kind, key) {
    if (!DEBUG || _logged.has(kind + key)) return;
    _logged.add(kind + key);
    console.warn("[route-i18n] " + kind + " (" + LOCALE + "): " + JSON.stringify(key));
  }

  function norm(s) {
    return String(s)
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/\s+/g, " ")
      .trim();
  }

  function writeNode(node, raw, value) {
    var lead = raw.match(/^\s*/)[0];
    var trail = raw.match(/\s*$/)[0];
    var next = lead + value + trail;
    if (node.nodeValue !== next) node.nodeValue = next;
  }

  function translateTextNode(node) {
    var raw = node.nodeValue;
    var key = norm(raw);
    if (!key) return;

    if (TEXT.has(key)) {
      var val = TEXT.get(key);
      if (val == null) { logDrift("TODO untranslated key", key); return; } // leave English
      writeNode(node, raw, val);
      return;
    }

    for (var i = 0; i < PATTERNS.length; i++) {
      var p = PATTERNS[i];
      if (!p || p.tr == null) continue;
      if (p.re.test(key)) { writeNode(node, raw, key.replace(p.re, p.tr)); return; }
    }

    // Unmatched: only interesting if it looks like English copy and isn't already translated.
    if (/[A-Za-z]/.test(key) && !VALUE_SET.has(key)) logDrift("Unknown string (drift?)", key);
  }

  function translateAriaLabel(el) {
    if (!el || el.nodeType !== 1 || !el.hasAttribute("aria-label")) return;
    var key = norm(el.getAttribute("aria-label"));
    if (!ARIA.has(key)) return;
    var value = ARIA.get(key);
    if (value != null && el.getAttribute("aria-label") !== value) el.setAttribute("aria-label", value);
  }

  function translateWithin(root) {
    try {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      for (var node = walker.nextNode(); node; node = walker.nextNode()) translateTextNode(node);
      translateAriaLabel(root);
      root.querySelectorAll("[aria-label]").forEach(translateAriaLabel);
    } catch (e) { /* fail-safe */ }
  }

  function translateAll() {
    try {
      document.querySelectorAll(SELECTORS).forEach(translateWithin);
    } catch (e) { /* fail-safe */ }
  }

  // --- Observer & bootstrap ------------------------------------------------------------------
  // Body-level scope is required because Route inserts widgets next to the checkout button and
  // renders the modal as a body-level portal. The callback only schedules a debounced pass, so
  // the broad observer stays cheap; translateAll is itself scoped to SELECTORS.
  var pending = null;
  function scheduleTranslate() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; translateAll(); }, DEBOUNCE_MS);
  }

  function start() {
    try {
      translateAll();
      new MutationObserver(scheduleTranslate).observe(document.body, {
        childList: true, subtree: true, characterData: true
      });
      if (DEBUG) console.info("[route-i18n] active — locale:", LOCALE);
    } catch (e) { /* fail-safe */ }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
// END ROUTE CODE